<template>
    <b-container fluid class="px-md-5">
        <h4 class="mt-4 p-2 text-center text-primary text-uppercase">Módulo de Cumplimiento AML</h4>

        <!-- Stats -->
        <b-row class="mb-3">
            <b-col cols="6" md="3" class="mb-2">
                <b-card class="text-center shadow-sm border-0 h-100" body-class="py-3">
                    <h3 class="mb-0 text-danger">{{ stats.new }}</h3>
                    <small class="text-muted">Alertas Nuevas</small>
                </b-card>
            </b-col>
            <b-col cols="6" md="3" class="mb-2">
                <b-card class="text-center shadow-sm border-0 h-100" body-class="py-3">
                    <h3 class="mb-0 text-warning">{{ stats.single }}</h3>
                    <small class="text-muted">Transacción &gt; $999</small>
                </b-card>
            </b-col>
            <b-col cols="6" md="3" class="mb-2">
                <b-card class="text-center shadow-sm border-0 h-100" body-class="py-3">
                    <h3 class="mb-0 text-info">{{ stats.cumulative }}</h3>
                    <small class="text-muted">Alertas Acumuladas</small>
                </b-card>
            </b-col>
            <b-col cols="6" md="3" class="mb-2">
                <b-card class="text-center shadow-sm border-0 h-100" body-class="py-3">
                    <h3 class="mb-0 text-success">{{ stats.reviewed }}</h3>
                    <small class="text-muted">Revisadas</small>
                </b-card>
            </b-col>
        </b-row>

        <b-tabs v-model="activeTab" content-class="mt-3">

            <!-- Tab Alertas -->
            <b-tab title="Alertas" active>
                <b-row class="mb-2">
                    <b-col cols="12" md="4" class="mb-1">
                        <b-form-input v-model="alertSearch" size="sm" placeholder="Buscar por nombre o documento..." @input="loadAlerts"></b-form-input>
                    </b-col>
                    <b-col cols="6" md="3" class="mb-1">
                        <b-form-select v-model="alertStatus" size="sm" :options="statusOptions" @change="loadAlerts"></b-form-select>
                    </b-col>
                    <b-col cols="6" md="3" class="mb-1">
                        <b-form-select v-model="alertType" size="sm" :options="typeOptions" @change="loadAlerts"></b-form-select>
                    </b-col>
                </b-row>

                <b-table :items="alerts" :fields="alertFields" responsive="sm" hover small show-empty
                    :busy="loadingAlerts" empty-text="No hay alertas" table-variant="light">

                    <template #cell(alert_type)="row">
                        <b-badge :variant="row.item.alert_type === 'single_transaction' ? 'warning' : 'info'">
                            {{ row.item.alert_type === 'single_transaction' ? 'Individual' : 'Acumulado' }}
                        </b-badge>
                    </template>
                    <template #cell(sender)="row">
                        <strong>{{ row.item.sender_name }}</strong><br>
                        <small class="text-muted">{{ row.item.sender_nd }}</small>
                    </template>
                    <template #cell(threshold)="row">
                        <span class="text-muted">USD {{ formatMoney(row.item.threshold) }}</span>
                    </template>
                    <template #cell(amount_usd)="row">
                        <strong class="text-danger">USD {{ formatMoney(row.item.amount_usd) }}</strong>
                    </template>
                    <template #cell(status)="row">
                        <b-badge :variant="statusVariant(row.item.status)">{{ statusText(row.item.status) }}</b-badge>
                    </template>
                    <template #cell(created_at)="row">
                        <small>{{ formatDate(row.item.created_at) }}</small>
                    </template>
                    <template #cell(actions)="row">
                        <b-button size="sm" variant="outline-primary" @click="openReview(row.item)" title="Revisar">
                            <i class="material-icons" style="font-size:16px;">rate_review</i>
                        </b-button>
                        <b-button size="sm" variant="outline-secondary" @click="goToSender(row.item.sender_nd)" title="Ver remitente">
                            <i class="material-icons" style="font-size:16px;">person</i>
                        </b-button>
                    </template>
                    <template #table-busy>
                        <div class="text-center text-primary my-3"><b-spinner class="align-middle mr-1"></b-spinner><strong>Cargando...</strong></div>
                    </template>
                </b-table>

                <b-pagination v-model="alertPage" :total-rows="alertTotal" :per-page="50"
                    align="center" size="sm" class="mt-2" @change="loadAlerts"></b-pagination>
            </b-tab>

            <!-- Tab Remitentes -->
            <b-tab title="Remitentes">
                <b-row class="mb-2">
                    <b-col cols="12" md="6">
                        <b-input-group size="sm">
                            <b-form-input v-model="senderSearch" placeholder="Nombre, RUT, documento..." @keyup.enter="loadSenders"></b-form-input>
                            <b-input-group-append>
                                <b-button variant="primary" @click="loadSenders()"><i class="material-icons" style="font-size:16px;">search</i></b-button>
                                <b-button variant="default" :disabled="!senderSearch" @click="senderSearch=''; loadSenders()"><i class="material-icons" style="font-size:16px;">close</i></b-button>
                            </b-input-group-append>
                        </b-input-group>
                    </b-col>
                </b-row>

                <b-table :items="senders" :fields="senderFields" responsive="sm" hover small show-empty
                    :busy="loadingSenders" empty-text="No hay remitentes" table-variant="light">

                    <template #cell(name)="row">
                        <strong>{{ row.item.sender_name }}</strong><br>
                        <small class="text-muted">{{ row.item.td }}: {{ row.item.nd }}</small>
                    </template>
                    <template #cell(total_usd_6m)="row">
                        <span :class="row.item.total_usd_6m > 4999 ? 'text-danger font-weight-bold' : ''">
                            USD {{ formatMoney(row.item.total_usd_6m) }}
                        </span><br>
                        <small class="text-muted">{{ row.item.payments_6m }} envíos</small>
                    </template>
                    <template #cell(total_usd)="row">
                        <small>USD {{ formatMoney(row.item.total_usd) }}</small><br>
                        <small class="text-muted">{{ row.item.total_payments }} envíos</small>
                    </template>
                    <template #cell(open_alerts)="row">
                        <b-badge :variant="row.item.open_alerts > 0 ? 'danger' : 'success'">{{ row.item.open_alerts }}</b-badge>
                        <small v-if="row.item.total_alerts > 0" class="text-muted ml-1">({{ row.item.total_alerts }} total)</small>
                    </template>
                    <template #cell(actions)="row">
                        <b-button size="sm" variant="outline-primary" @click="goToSender(row.item.nd)">
                            <i class="material-icons" style="font-size:16px;">visibility</i>
                        </b-button>
                    </template>
                    <template #table-busy>
                        <div class="text-center text-primary my-3"><b-spinner class="align-middle mr-1"></b-spinner><strong>Cargando...</strong></div>
                    </template>
                </b-table>

                <b-pagination v-model="senderPage" :total-rows="senderTotal" :per-page="50"
                    align="center" size="sm" class="mt-2" @change="loadSenders"></b-pagination>
            </b-tab>
        </b-tabs>

        <!-- Modal Revisar Alerta -->
        <b-modal id="review-modal" title="Revisar Alerta de Cumplimiento" centered no-fade
            header-bg-variant="primary" header-text-variant="light" header-close-variant="light"
            footer-class="border-0" @ok="submitReview">
            <div v-if="selectedAlert">
                <b-row class="mb-2">
                    <b-col cols="6">
                        <small class="text-muted">Tipo</small>
                        <p class="mb-1">
                            <b-badge :variant="selectedAlert.alert_type === 'single_transaction' ? 'warning' : 'info'">
                                {{ selectedAlert.alert_type === 'single_transaction' ? 'Individual' : 'Acumulado' }}
                            </b-badge>
                        </p>
                    </b-col>
                    <b-col cols="6">
                        <small class="text-muted">Monto</small>
                        <p class="mb-1 font-weight-bold text-danger">USD {{ formatMoney(selectedAlert.amount_usd) }}</p>
                    </b-col>
                    <b-col cols="6">
                        <small class="text-muted">Umbral</small>
                        <p class="mb-1">USD {{ formatMoney(selectedAlert.threshold) }}</p>
                    </b-col>
                    <b-col cols="6">
                        <small class="text-muted">Fecha</small>
                        <p class="mb-1">{{ formatDate(selectedAlert.created_at) }}</p>
                    </b-col>
                    <b-col cols="12">
                        <small class="text-muted">Remitente</small>
                        <p class="mb-1">{{ selectedAlert.sender_name }} — {{ selectedAlert.sender_nd }}</p>
                    </b-col>
                </b-row>
                <b-form-group label="Nuevo estado">
                    <b-form-select v-model="reviewForm.status" :options="reviewStatusOptions" size="sm"></b-form-select>
                </b-form-group>
                <b-form-group label="Notas del oficial de cumplimiento">
                    <b-form-textarea v-model="reviewForm.notes" rows="3" placeholder="Observaciones, acciones tomadas, referencias UAF..."></b-form-textarea>
                </b-form-group>
            </div>
        </b-modal>
    </b-container>
</template>

<script>
import axios from 'axios';

export default {
    name: 'ComplianceIndex',
    data: () => ({
        activeTab: 0,
        stats: { new: 0, reviewed: 0, single: 0, cumulative: 0, total: 0 },

        alerts: [],
        alertFields: [
            { key: 'alert_type', label: 'Tipo' },
            { key: 'sender', label: 'Remitente' },
            { key: 'threshold', label: 'Umbral' },
            { key: 'amount_usd', label: 'Monto USD' },
            { key: 'status', label: 'Estado' },
            { key: 'created_at', label: 'Fecha' },
            { key: 'actions', label: '' },
        ],
        alertSearch: '', alertStatus: 'all', alertType: 'all',
        alertPage: 1, alertTotal: 0, loadingAlerts: false,
        statusOptions: [
            { value: 'all', text: 'Todos los estados' },
            { value: '0',   text: 'Nuevas' },
            { value: '1',   text: 'Revisadas' },
            { value: '2',   text: 'Descartadas' },
        ],
        typeOptions: [
            { value: 'all',                text: 'Todos los tipos' },
            { value: 'single_transaction', text: 'Transacción Individual' },
            { value: 'cumulative',         text: 'Acumulado' },
        ],

        selectedAlert: null,
        reviewForm: { status: 1, notes: '' },
        reviewStatusOptions: [
            { value: 1, text: 'Revisada' },
            { value: 2, text: 'Descartada' },
            { value: 0, text: 'Volver a Nueva' },
        ],

        senders: [],
        senderFields: [
            { key: 'name',        label: 'Remitente' },
            { key: 'total_usd_6m', label: 'Total 6 meses (USD)' },
            { key: 'total_usd',   label: 'Total histórico (USD)' },
            { key: 'open_alerts', label: 'Alertas abiertas' },
            { key: 'actions',     label: '' },
        ],
        senderSearch: '', senderPage: 1, senderTotal: 0, loadingSenders: false,
    }),
    mounted() {
        this.loadStats();
        this.loadAlerts();
        this.loadSenders();
    },
    methods: {
        loadStats() {
            axios.get('/api/compliance/stats').then(r => { this.stats = r.data; });
        },
        loadAlerts(page) {
            if (page) this.alertPage = page;
            this.loadingAlerts = true;
            axios.get('/api/compliance/alerts', {
                params: { search: this.alertSearch, status: this.alertStatus, type: this.alertType, page: this.alertPage }
            }).then(r => {
                this.alerts = r.data.data;
                this.alertTotal = r.data.total;
            }).finally(() => { this.loadingAlerts = false; });
        },
        loadSenders(page) {
            if (page) this.senderPage = page;
            this.loadingSenders = true;
            axios.get('/api/compliance/senders', {
                params: { search: this.senderSearch, page: this.senderPage }
            }).then(r => {
                this.senders = r.data.data;
                this.senderTotal = r.data.total;
            }).finally(() => { this.loadingSenders = false; });
        },
        goToSender(nd) {
            this.$router.push({ name: 'ComplianceSender', query: { nd } });
        },
        openReview(alert) {
            this.selectedAlert = alert;
            this.reviewForm = { status: 1, notes: alert.notes || '' };
            this.$bvModal.show('review-modal');
        },
        submitReview(bvModalEvent) {
            bvModalEvent.preventDefault();
            axios.put(`/api/compliance/alerts/${this.selectedAlert.id}/review`, this.reviewForm).then(() => {
                this.$bvModal.hide('review-modal');
                this.loadAlerts();
                this.loadStats();
                this.$root.$bvToast.toast('Alerta actualizada correctamente.', {
                    title: 'Cumplimiento', variant: 'success',
                    toaster: 'b-toaster-top-right', autoHideDelay: 4000, solid: true,
                });
            });
        },
        formatMoney(val) {
            return Number(val || 0).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        formatDate(val) {
            if (!val) return '—';
            return new Date(val).toLocaleString('es-CL');
        },
        statusVariant(s) { return s === 0 ? 'danger' : s === 1 ? 'success' : 'secondary'; },
        statusText(s)    { return s === 0 ? 'Nueva' : s === 1 ? 'Revisada' : 'Descartada'; },
    },
};
</script>
